/**
 * Odkud je která adresa dosažitelná — a odkud se dívá ten, kdo kouká na UI.
 *
 * Filip 2026-09-08 k tlačítku „otevřít v ACM" u Supabase: „nerozlišuje venkovní
 * a vnitřní adresu. Zobrazuje se jen tak bez komentáře i lidem, co přijdou přes
 * venkovní URL, když supabase má jen vnitřní."
 *
 * Instance nabízí až tři adresy: `raw_*` (`http://127.0.0.1:port`, funguje jen
 * na samotném uzlu), `auth_*` (ACM nginx wrapper se SSO na vnitřní adrese uzlu)
 * a `public_*` (publikovaná URL). UI je nabízelo v pořadí public → auth → raw
 * bez ohledu na to, že návštěvník z internetu se na vnitřní adresu nedostane.
 * Výsledek: tlačítko, které nikam nevede, a žádné vysvětlení proč.
 */

export type Reach = 'public' | 'internal' | 'host-only' | 'unknown';

const PRIVATE_V4 =
  /^(10\.|127\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/;

/** Klasifikace hostitele adresy. Nezná síťovou topologii, jen tvar jména/IP. */
export function reachOfHost(hostname: string): Reach {
  const h = (hostname || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
  if (!h) return 'unknown';
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return 'host-only';
  if (PRIVATE_V4.test(h)) return 'internal';
  if (h.startsWith('fc') || h.startsWith('fd')) return 'internal'; // IPv6 ULA
  if (h.endsWith('.local') || h.endsWith('.internal') || !h.includes('.')) return 'internal';
  return 'public';
}

/** Klasifikace celé URL; nevalidní URL = `unknown`. */
export function reachOfUrl(url: string | null | undefined): Reach {
  if (!url) return 'unknown';
  try {
    return reachOfHost(new URL(url).hostname);
  } catch {
    return 'unknown';
  }
}

/** Odkud se dívá uživatel (podle adresy, na které má otevřené UI). */
export function viewerReach(href?: string): Reach {
  const src = href ?? (typeof globalThis.location !== 'undefined' ? globalThis.location.href : '');
  return reachOfUrl(src || null);
}

/**
 * Dostane se návštěvník na tuhle adresu z místa, kde je?
 *
 * Pravidlo je záměrně opatrné: z veřejné adresy se na vnitřní ani na
 * `127.0.0.1` nedostane nikdy. Z vnitřní sítě se na `127.0.0.1` taky nedostane
 * (to je adresa uzlu, ne návštěvníka). Zbytek považujeme za dosažitelný —
 * VPN a rozdělené sítě z prohlížeče poznat nejde a falešné blokování by
 * uškodilo víc než opatrná nabídka.
 */
export function isReachableFrom(target: Reach, viewer: Reach): boolean {
  if (target === 'unknown' || viewer === 'unknown') return true;
  if (target === 'host-only') return false;
  if (target === 'internal') return viewer === 'internal' || viewer === 'host-only';
  return true; // public
}

export interface LinkChoice<T extends string = string> {
  kind: T;
  url: string | null | undefined;
}

/**
 * Vybere první adresu, na kterou návštěvník dosáhne, a zároveň řekne, co
 * muselo být přeskočeno — UI to má vysvětlit, ne mlčet.
 */
export function pickReachable<T extends string>(
  choices: LinkChoice<T>[],
  viewer: Reach = viewerReach(),
): {
  chosen: LinkChoice<T> | null;
  chosenReach: Reach;
  skipped: Array<{ kind: T; reach: Reach }>;
} {
  const skipped: Array<{ kind: T; reach: Reach }> = [];
  for (const c of choices) {
    if (!c.url) continue;
    const r = reachOfUrl(c.url);
    if (isReachableFrom(r, viewer)) return { chosen: c, chosenReach: r, skipped };
    skipped.push({ kind: c.kind, reach: r });
  }
  return { chosen: null, chosenReach: 'unknown', skipped };
}
